<template>
  <div class="app-instance-types">
    <h4>{{ $t("WorkspaceEdit.appDetails.widgetTitle.instanceType") }}</h4>

    <p class="app-instance-types__label">
      {{ t("description") }}
    </p>

    <div class="app-instance-types__content">
      <span class="app-instance-types__label">{{ t("instanceTypes") }}</span>

      <div
        :class="[
          'app-instance-types__row',
          'app-instance-types__row--gap-medium',
          'app-instance-types__row--align-start'
        ]"
      >
        <draggable
          v-model="instanceList"
          :disabled="instanceList.length === 0 || disabled"
          class="app-instance-types__list"
          ghost-class="app-instance-types__list-item--dragging-over"
          draggable=".app-instance-types__list-item--draggable"
          tag="ul"
        >
          <li
            v-for="instanceType in instanceList"
            :key="instanceType"
            :class="['app-instance-types__list-item', isManagingInstance && 'app-instance-types__list-item--draggable']"
          >
            <span class="app-instance-types__item-label">
              <v-icon v-if="isManagingInstance && instanceList.length > 1">navigate_view_list</v-icon>
              {{ instanceType }}
            </span>

            <span
              v-if="isDeployed(instanceType)"
              :class="[
                'app-instance-types__item-label',
                isManagingInstance ? 'app-instance-types__label' : 'app-instance-types__item-label--status-deployed'
              ]"
            >
              <span class="app-instance-types__row">
                <v-icon v-if="!isManagingInstance">action_upload_to_cloud</v-icon>
                <span v-if="!isManagingInstance">Deployed</span>
                <span v-else>Currently deployed</span>

                <v-btn
                  v-if="isManagingInstance"
                  @click="removeInstance(instanceType)"
                  :disabled="instanceList.length <= 1 || loader.on"
                  icon
                >
                  <v-icon>action_trash</v-icon>
                </v-btn>
              </span>
            </span>

            <span
              v-else-if="isProspect(instanceType) && !isDraft"
              :class="[
                'app-instance-types__item-label',
                'app-instance-types__row',
                'app-instance-types__item-label--status-prospect'
              ]"
            >
              <span>{{ t("toDeploy") }}</span>

              <v-btn
                v-if="isManagingInstance"
                @click="removeInstance(instanceType)"
                :disabled="instanceList.length <= 1 || loader.on"
                icon
              >
                <v-icon>action_trash</v-icon>
              </v-btn>
            </span>

            <v-btn
              v-else-if="isManagingInstance"
              @click="removeInstance(instanceType)"
              :disabled="instanceList.length <= 1 || loader.on"
              icon
            >
              <v-icon>action_trash</v-icon>
            </v-btn>
          </li>
        </draggable>

        <div
          v-if="isManagingInstance && !isDraft"
          :class="['app-instance-types__column', 'app-instance-types__column--gap']"
        >
          <v-btn
            v-for="instance in instanceList"
            :key="`btn-${instance}`"
            :text="!(isProspect(instance) || (isDeployed(instance) && !prospectNextInstance))"
            :disabled="isProspect(instance) || isDeployed(instance)"
            :class="['app-instance-types__button--min-height']"
            @click="chooseProspectInstance(instance)"
          >
            {{ t("chooseToDeploy") }}
          </v-btn>
        </div>
      </div>
    </div>

    <div v-if="isManagingInstance" class="app-instance-types__controller">
      <v-label>
        <span class="app-instance-types__label">{{ t("addInstanceType") }}</span>

        <div class="app-instance-types__row">
          <v-select v-model="instanceToInsert" :items="modelInstances" hide-details />

          <v-btn @click="addSelectedInstance" :disabled="!instanceToInsert" icon>
            <v-icon>action_add_circle</v-icon>
          </v-btn>
        </div>
      </v-label>

      <div class="app-instance-types__row app-instance-types__row--spaced app-instance-types__buttons">
        <v-btn @click="toggleModifyingInstance">Cancel</v-btn>
        <v-btn @click="updateInstanceList" color="primary" :disabled="!wasInstanceListModified">
          <span v-if="isDraft">{{ t("save") }}</span>
          <span v-else>{{ t("saveAndDeploy") }}</span>
        </v-btn>
      </div>
    </div>

    <div v-else class="app-instance-types__row app-instance-types__buttons">
      <v-btn @click="toggleModifyingInstance" :loading="loader.on">{{ t("manageInstanceTypes") }}</v-btn>
    </div>
  </div>
</template>

<script lang="ts">
import { IInstanceType, IWorkspaceAsset, Service, WorkspaceStatusEnum } from "@/types/workspace"
import { defineComponent, ref, computed, PropType } from "@vue/composition-api"
import { createTranslationModule } from "@/plugins/i18n"
import { useStore } from "@/composable/useStore"
import Draggable from "vuedraggable"
import Loader from "@/utils/loader"
import "../../scss/_instance-types.scss"

export default defineComponent({
  name: "InstanceTypes",
  props: {
    app: {
      type: Object as PropType<IWorkspaceAsset | Service>,
      required: true
    },
    allowedInstances: {
      type: Array as PropType<IInstanceType[]>,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  setup(props, ctx) {
    const { spaceModule } = useStore()
    const t = createTranslationModule("WorkspaceEdit.instanceTypes")
    const loader = Loader()
    const instanceToInsert = ref("")
    const isManagingInstance = ref(false)
    const prospectNextInstance = ref<string | null>(null)
    const instanceList = ref<string[]>([])

    const app = computed(() => {
      return props.app as IWorkspaceAsset
    })

    const modelInstances = computed(() => {
      const model = spaceModule.assetModels.find(model => model.key === app.value.type)

      if (model && model.availableInstanceTypes) {
        const instanceNames = model.availableInstanceTypes.map(i => i.instanceTypes.map(t => t.code)).flat()

        return instanceNames.filter(instanceName =>
          instanceList.value.every(allowedInstance => {
            return allowedInstance !== instanceName
          })
        )
      }

      return []
    })

    const appInstances = computed(() => {
      const allowedInstances = app.value?.allowedInstanceTypes || []
      return (
        allowedInstances.map(i => {
          return i.code
        }) || []
      )
    })

    const deployedInstance = computed(() => {
      return app.value.deployedInstanceType
    })

    const wasInstanceListModified = computed(() => {
      const listModified = !instanceList.value.every((instance, index) => instance === appInstances.value[index])
      const appCountMatchesInstanceList = instanceList.value.length === appInstances.value.length

      if (listModified || !appCountMatchesInstanceList) {
        return true
      }

      if (isDraft.value) {
        return _isDeployModified.value
      } else {
        return prospectNextInstance.value !== null
      }
    })

    const _isDeployModified = computed(() => {
      return prospectNextInstance.value !== null && prospectNextInstance.value !== deployedInstance.value?.code
    })

    const isDraft = computed(() => {
      return spaceModule.watching?.status === WorkspaceStatusEnum.Draft
    })

    const isDeployed = (instance: string) => {
      return instance === deployedInstance.value?.code
    }

    const isProspect = (instance: string) => {
      if (prospectNextInstance.value) {
        if (deployedInstance.value?.code !== prospectNextInstance.value) {
          return prospectNextInstance.value === instance
        }
      }

      return false
    }

    const addSelectedInstance = () => {
      if (instanceToInsert.value) {
        instanceList.value.push(instanceToInsert.value)
        instanceToInsert.value = ""
      }
    }

    const removeInstance = (instance: string) => {
      if (instanceList.value.length > 1) {
        instanceList.value = instanceList.value.filter(i => i !== instance)
      }
    }

    const chooseProspectInstance = (instance: string) => {
      prospectNextInstance.value = instance
    }

    const toggleModifyingInstance = () => {
      isManagingInstance.value = !isManagingInstance.value

      if (!isManagingInstance.value) {
        instanceList.value = [...appInstances.value]
        prospectNextInstance.value = null
      }
    }

    const updateInstanceList = () => {
      const shouldReplaceInstanceWithProspect =
        prospectNextInstance.value && !isDeployed(prospectNextInstance.value as string)

      loader.run(async () => {
        isManagingInstance.value = false

        await spaceModule.updateApp(app.value.id, {
          allowedInstanceTypes: instanceList.value.map(instance => {
            return { code: instance }
          }),
          description: app.value.description
        })

        if (shouldReplaceInstanceWithProspect) {
          await spaceModule.deployDecoupledInstanceType({
            workspaceId: spaceModule.watching.id,
            assetId: app.value.id,
            desiredInstanceType: prospectNextInstance.value as string
          })
        }

        ctx.emit("update")
      })
    }

    loader.run(async () => {
      instanceList.value = [...appInstances.value]
    })

    return {
      t,
      loader,
      isDraft,
      wasInstanceListModified,
      removeInstance,
      prospectNextInstance,
      chooseProspectInstance,
      addSelectedInstance,
      instanceList,
      toggleModifyingInstance,
      isProspect,
      isManagingInstance,
      isDeployed,
      instanceToInsert,
      updateInstanceList,
      modelInstances,
      appInstances
    }
  },
  components: {
    Draggable
  }
})
</script>
